import React, { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from './Layout';
import { Header } from './Header';
import { BottomNavigation } from './BottomNavigation';
import { useAuthStore } from '@stores/authStore';
import { useAuth } from '@providers';
import { getProfilePath } from '@utils/slugHelpers';

/**
 * StandardLayout — declarative route layout wrapper for authenticated users.
 * Wraps routes that use the standard header/footer; uses React Router's
 * <Outlet> to render child routes.
 *
 * Features:
 * - Authenticated Header with notifications + user avatar
 * - BottomNavigation with Connect path based on user profile
 * - Natural scroll for feed/list screens
 * - Dynamic gradient based on scroll position (home only, via IntersectionObserver)
 *
 * Architecture: provides user data + connect path to Header and
 * BottomNavigation. Badge polling is owned by `BadgeProvider` (mounted in
 * `RootLayout`), which Header / BottomNavigation read from via the
 * `*Value` subscriber hooks — this layout doesn't own that lifecycle.
 */
export const StandardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showAuthenticatedUI, cachedUserInfo } = useAuth();

  // Get user data from stores
  const { user } = useAuthStore();

  // Calculate user's profile path for Connect button
  // Extract primitives to satisfy React Compiler dependency analysis
  const cachedId = cachedUserInfo?.id;
  const cachedSlug = cachedUserInfo?.slug;
  const connectPath = useMemo(() => {
    if (user) {
      return getProfilePath({ id: user.id, slug: user.slug });
    }
    if (cachedId) {
      return getProfilePath({ id: cachedId, slug: cachedSlug ?? undefined });
    }
    return undefined;
  }, [cachedId, cachedSlug, user]);

  const isHomeFeed = location.pathname === '/home';
  const showBottomNav = ['/home', '/events', '/messages', '/notifications'].includes(
    location.pathname,
  );
  const showDefaultHeader = location.pathname !== '/events';

  // Home manages its own gradient below the sticky tabs — disable Layout gradient
  const gradientType: 'none' | 'subtle' | 'standard' | 'prominent' = isHomeFeed
    ? 'none'
    : 'standard';

  // Navigation handlers for header
  const handleAvatarClick = useCallback(() => {
    if (connectPath) {
      navigate(connectPath);
    }
  }, [connectPath, navigate]);

  const handleNotificationsClick = useCallback(() => {
    navigate('/notifications');
  }, [navigate]);

  // Custom header with authenticated user data.
  // Header reads showAuthenticatedUI/isAuthenticated from useAuth() directly,
  // so we no longer pass it as a prop.
  const customHeader = showDefaultHeader ? (
    <Header
      user={showAuthenticatedUI ? user : undefined}
      cachedUserInfo={showAuthenticatedUI ? cachedUserInfo : undefined}
      onAvatarClick={handleAvatarClick}
      onNotificationsClick={handleNotificationsClick}
    />
  ) : undefined;

  const customFooter = showBottomNav ? <BottomNavigation connectPath={connectPath} /> : undefined;

  return (
    <Layout
      showDefaultHeader={false}
      customHeader={customHeader}
      gradientType={gradientType}
      showDefaultFooter={false}
      customFooter={customFooter}
    >
      <Outlet />
    </Layout>
  );
};
